<template>
  <b-modal
    ref="new-teacher-notification"
    id="new-teacher-notification"
    hide-header
    hide-footer
    centered
    no-close-on-backdrop
    @shown="handleShown"
    @hidden="handleHidden"
  >
    <b-row>
      <b-col md="12">
        <h4 class="purple mb-4">{{ modalTitle }}</h4>
      </b-col>

      <b-col md="12" class="mt-2">
        <b-form-group label="Destinatário">
          <b-form-input
            type="text"
            :value="teacher && teacher.User ? teacher.User.name : ''"
            disabled
          ></b-form-input>
        </b-form-group>
      </b-col>

      <b-col md="6" class="mt-2">
        <b-form-group label="Ciclo">
          <multiselect
            v-model="selectedCycle"
            :options="cyclesOptions"
            :multiple="false"
            placeholder="Selecione o ciclo"
            label="name"
            track-by="id"
            selectLabel=""
          >
            <span slot="noResult">Nada encontrado</span>
          </multiselect>
        </b-form-group>
      </b-col>

      <b-col md="6" class="mt-2">
        <b-form-group label="Aluno (opcional)">
          <multiselect
            v-model="selectedStudent"
            :options="filteredStudents"
            :multiple="false"
            placeholder="Selecione o aluno"
            label="name"
            track-by="id"
            selectLabel=""
          >
            <template #singleLabel="{ option }">
              <span>{{ option.name }}</span>
              <span class="cycle-tag">{{ option.cycle_name }}</span>
            </template>
            <template #option="{ option }">
              <span>{{ option.name }}</span>
              <span class="cycle-tag">{{ option.cycle_name }}</span>
            </template>
            <span slot="noResult">Nada encontrado</span>
          </multiselect>
        </b-form-group>
      </b-col>

      <b-col md="6" class="mt-2">
        <b-form-group label="Área do Conhecimento">
          <multiselect
            v-model="selectedSubject"
            :options="subjectsOptions"
            :multiple="false"
            :disabled="!selectedCycle"
            placeholder="Selecine a área do conhecimento"
            label="name"
            track-by="id"
            selectLabel=""
          >
            <span slot="noResult">Nada encontrado</span>
          </multiselect>
        </b-form-group>
      </b-col>

      <b-col md="6" class="mt-2">
        <b-form-group label="Data limite (opcional)">
          <b-form-input type="date" v-model="deadlineDate"></b-form-input>
        </b-form-group>
      </b-col>

      <b-col md="12" class="mt-2">
        <b-form-group label="Mensagem">
          <b-form-textarea
            v-model="message"
            maxlength="500"
            rows="4"
            placeholder="Digite a mensagem da notificação"
          ></b-form-textarea>
          <span class="chars-count">{{ message.length }}/500</span>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="mt-5">
      <b-col>
        <b-button class="btn large outline" @click="cancel" :disabled="loading"
          >Cancelar</b-button
        >
      </b-col>
      <b-col class="text-right">
        <b-button
          class="btn large blue"
          @click="submit"
          :disabled="!isFormValid || loading"
        >
          <b-spinner v-if="loading" small variant="light" label="Aguarde!">
          </b-spinner>
          <span v-else>{{ submitLabel }}</span>
        </b-button>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import moment from "moment-timezone";
import httpRda from "../../http-rda";
import TeacherNotificationsService from "@/services/teacherNotifications.service";

export default {
  name: "",

  props: {
    teacher: {
      type: Object,
      default: null,
    },
    notification: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      students: [],
      selectedCycle: null,
      selectedStudent: null,
      selectedSubject: null,
      deadlineDate: "",
      message: "",
      prefilling: false,
    };
  },

  watch: {
    selectedCycle(cycle) {
      if (this.prefilling) {
        return;
      }

      this.selectedSubject = null;

      if (
        this.selectedStudent &&
        (!cycle || this.selectedStudent.cycle_id !== cycle.id)
      ) {
        this.selectedStudent = null;
      }
    },

    selectedStudent(student) {
      if (this.prefilling) {
        return;
      }

      if (
        student &&
        (!this.selectedCycle || this.selectedCycle.id !== student.cycle_id)
      ) {
        this.selectedCycle =
          this.cyclesOptions.find((cycle) => cycle.id === student.cycle_id) ||
          this.selectedCycle;
      }
    },

    teacher: {
      immediate: true,
      handler(teacher) {
        if (teacher) this.fetchStudents();
      },
    },
  },

  computed: {
    isEditMode() {
      return !!this.notification;
    },

    modalTitle() {
      return this.isEditMode ? "Editar notificação" : "Nova notificação";
    },

    submitLabel() {
      return this.isEditMode ? "Salvar" : "Enviar";
    },

    cyclesOptions() {
      return (this.teacher && this.teacher.Cycles) || [];
    },

    filteredStudents() {
      if (!this.selectedCycle) return this.students;

      return this.students.filter(
        (student) => student.cycle_id === this.selectedCycle.id,
      );
    },

    subjectsOptions() {
      if (!this.selectedCycle) return [];

      const subjects = (this.teacher && this.teacher.Subjects) || [];

      const inCycle = subjects.filter(
        (subject) => subject.cycle_id === this.selectedCycle.id,
      );

      const uniqueById = inCycle.reduce((acc, subject) => {
        if (!acc.has(subject.id)) {
          acc.set(subject.id, { id: subject.id, name: subject.designation });
        }
        return acc;
      }, new Map());

      return Array.from(uniqueById.values());
    },

    isFormValid() {
      return !!(
        this.selectedCycle &&
        this.selectedSubject &&
        this.message.trim()
      );
    },
  },

  methods: {
    reset() {
      this.selectedCycle = null;
      this.selectedStudent = null;
      this.selectedSubject = null;
      this.deadlineDate = "";
      this.message = "";
      this.loading = false;
      this.prefilling = false;
    },

    cancel() {
      this.$refs["new-teacher-notification"].hide();
    },

    populateForEdit(notification) {
      this.prefilling = true;

      this.selectedCycle =
        this.cyclesOptions.find(
          (cycle) => cycle.id === notification.cycle_id
        ) || null;
      this.selectedSubject =
        this.subjectsOptions.find(
          (subject) => subject.id === notification.subject_id
        ) || null;
      this.selectedStudent = notification.student_id
        ? this.filteredStudents.find(
            (student) => student.id === notification.student_id
          ) || null
        : null;
      this.deadlineDate = notification.deadline
        ? moment
            .tz(notification.deadline, "America/Sao_Paulo")
            .format("YYYY-MM-DD")
        : "";
      this.message = notification.message || "";

      this.$nextTick(() => {
        this.prefilling = false;
      });
    },

    handleShown() {
      if (this.isEditMode) {
        this.populateForEdit(this.notification);
      }
    },

    handleHidden() {
      this.reset();
      this.$emit("closed");
    },

    async fetchStudents() {
      try {
        const response = await httpRda.get("/students-opt", {
          params: { teacher_id: this.teacher.id },
        });

        this.students = response.data.students;
      } catch (error) {
        this.students = [];
      }
    },

    async submit() {
      if (!this.isFormValid) return;

      this.loading = true;

      const payload = {
        cycle_id: this.selectedCycle.id,
        student_id: this.selectedStudent ? this.selectedStudent.id : null,
        subject_id: this.selectedSubject.id,
        deadline: this.deadlineDate
          ? moment.tz(this.deadlineDate, "America/Sao_Paulo").valueOf()
          : 0,
        message: this.message,
      };

      try {
        let response;

        if (this.isEditMode) {
          response = await TeacherNotificationsService.update(
            this.notification.id,
            payload
          );
          this.$emit("updated", response.data);
        } else {
          response = await TeacherNotificationsService.create({
            ...payload,
            teacher_id: this.teacher.id,
          });
          this.$emit("created", response.data);
        }

        const Toast = this.$swal.mixin({
          toast: true,
          position: "bottom-end",
          showConfirmButton: false,
          timer: 5000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener("mouseenter", this.$swal.stopTimer);
            toast.addEventListener("mouseleave", this.$swal.resumeTimer);
          },
        });

        Toast.fire({
          icon: "success",
          title: this.isEditMode
            ? "Notificação atualizada com sucesso!"
            : "Notificação enviada com sucesso!",
        });

        this.$refs["new-teacher-notification"].hide();
      } catch (error) {
        this.loading = false;

        const message =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          (this.isEditMode
            ? "Não foi possível atualizar a notificação!"
            : "Não foi possível enviar a notificação!");

        this.$swal.fire({
          icon: "error",
          title: "Oops...",
          text: message,
          footer: "Por favor, tente novamente mais tarde.",
        });
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/css/variables.scss";

div#new-teacher-notification {
  .modal-dialog {
    max-width: 820px;
    .modal-content {
      background-color: $grey2;
      border: 0;
      border-radius: 0;
      .modal-body {
        padding: 60px;
      }
    }
  }

  .chars-count {
    display: block;
    text-align: right;
    font-size: 12px;
    color: $grey4;
    margin-top: 4px;
  }

  .cycle-tag {
    display: inline-block;
    margin-left: 8px;
    padding: 2px 8px;
    border-radius: 5px;
    background-color: $blue;
    color: $white;
    font-size: 12px;
    font-weight: $bold;
    vertical-align: middle;
  }
}
</style>
